// Radha — Recurring Invoices / Subscriptions (Zoho Billing style). Define a
// template (customer, amount, cadence); "Generate due" turns every template past
// its next_run_date into a real invoice (migration 042) and advances the date.
import React, { useMemo, useState } from "react";

import {
  useRecurringInvoices, useCreateRecurringInvoice, useToggleRecurringInvoice, useGenerateDueInvoices,
  type Cadence, type RecurringInvoice,
} from "../hooks/useRecurringInvoices";
import { writeErrorMessage } from "../hooks/useCollectionsWrites";
import { ExportButton } from "../components/TableSearch";
import { useRole } from "../lib/authSupabase";
import { fmtMoney as inr, homeCurrency } from "../lib/money";


const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "9px 8px" };
const numTd: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };
const fld: React.CSSProperties = { padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)" };

const today = () => new Date().toISOString().slice(0, 10);

function CreateTemplate() {
  const create = useCreateRecurringInvoice();
  const [customer, setCustomer] = useState("");
  const [gstin, setGstin] = useState("");
  const [taxable, setTaxable] = useState("");
  const [tax, setTax] = useState("");
  const [cadence, setCadence] = useState<Cadence>("monthly");
  const [dueDays, setDueDays] = useState("30");
  const [start, setStart] = useState(today());
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const canSave = customer.trim() && Number(taxable) > 0;

  const submit = () => {
    setNote(null);
    create.mutate(
      {
        customer_name: customer.trim(), customer_gstin: gstin.trim() || null, place_of_supply: null,
        taxable_amount: Number(taxable), tax_amount: Number(tax) || 0, cadence, due_days: Number(dueDays) || 30,
        next_run_date: start,
      },
      {
        onSuccess: () => { setNote({ ok: true, text: "Template created" }); setCustomer(""); setGstin(""); setTaxable(""); setTax(""); },
        onError: (e) => setNote({ ok: false, text: writeErrorMessage(e) }),
      },
    );
  };

  return (
    <div style={{ border: "1px dashed var(--border-strong)", borderRadius: 10, padding: "12px 14px", marginBottom: 22, background: "var(--surface-2)" }}>
      <div style={{ font: "600 13px Inter", marginBottom: 8 }}>New recurring template</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Customer" style={{ ...fld, minWidth: 180 }} />
        <input value={gstin} onChange={(e) => setGstin(e.target.value)} placeholder="GSTIN (optional)" style={{ ...fld, width: 160 }} />
        <input value={taxable} onChange={(e) => setTaxable(e.target.value)} type="number" placeholder={`Taxable (${homeCurrency()})`} style={{ ...fld, width: 120 }} />
        <input value={tax} onChange={(e) => setTax(e.target.value)} type="number" placeholder={`Tax (${homeCurrency()})`} style={{ ...fld, width: 100 }} />
        <select value={cadence} onChange={(e) => setCadence(e.target.value as Cadence)} style={fld}>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="annual">Annual</option>
        </select>
        <input value={dueDays} onChange={(e) => setDueDays(e.target.value)} type="number" placeholder="Due (days)" style={{ ...fld, width: 100 }} />
        <input value={start} onChange={(e) => setStart(e.target.value)} type="date" style={fld} />
        <button onClick={submit} disabled={!canSave || create.isPending}
          style={{ padding: "6px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: canSave ? "pointer" : "default", fontSize: 13, opacity: canSave ? 1 : 0.5 }}>
          {create.isPending ? "Saving…" : "Create"}
        </button>
      </div>
      {note && <span style={{ display: "inline-block", marginTop: 8, fontSize: 13, color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>{note.text}</span>}
    </div>
  );
}

function TemplateRow({ t }: { t: RecurringInvoice }) {
  const toggle = useToggleRecurringInvoice();
  const overdue = t.is_active && t.next_run_date <= today();
  return (
    <tr style={{ borderTop: "1px solid var(--border)" }}>
      <td style={td}>{t.customer_name}</td>
      <td style={{ ...td, textTransform: "capitalize" }}>{t.cadence}</td>
      <td style={numTd}>{inr(Number(t.taxable_amount) + Number(t.tax_amount))}</td>
      <td style={{ ...td, color: overdue ? "var(--status-warning)" : "var(--fg)" }}>{t.next_run_date}{overdue ? " (due)" : ""}</td>
      <td style={{ ...td, color: "var(--fg-muted)" }}>{t.last_run_date ?? "—"}</td>
      <td style={td}>
        <button onClick={() => toggle.mutate({ id: t.id, is_active: !t.is_active })} disabled={toggle.isPending}
          style={{ padding: "3px 10px", fontSize: 12, border: `1px solid ${t.is_active ? "var(--status-good)" : "var(--border)"}`, color: t.is_active ? "var(--status-good)" : "var(--fg-muted)", background: "none", borderRadius: 6, cursor: "pointer" }}>
          {t.is_active ? "Active" : "Paused"}
        </button>
      </td>
    </tr>
  );
}

export function RecurringInvoicesPanel() {
  const { data, isLoading, error } = useRecurringInvoices();
  const generate = useGenerateDueInvoices();
  const { canWrite } = useRole();
  const [genNote, setGenNote] = useState<string | null>(null);

  const stats = useMemo(() => {
    const list = data ?? [];
    const active = list.filter((t) => t.is_active);
    const due = active.filter((t) => t.next_run_date <= today());
    const mrr = active.reduce((s, t) => {
      const amt = Number(t.taxable_amount) + Number(t.tax_amount);
      const monthly = t.cadence === "monthly" ? amt : t.cadence === "quarterly" ? amt / 3 : amt / 12;
      return s + monthly;
    }, 0);
    return { active: active.length, due: due.length, mrr };
  }, [data]);

  const runGenerate = () => {
    setGenNote(null);
    generate.mutate(undefined, {
      onSuccess: (n) => setGenNote(n > 0 ? `Generated ${n} invoice${n === 1 ? "" : "s"}` : "Nothing due"),
      onError: (e) => setGenNote(writeErrorMessage(e)),
    });
  };

  if (isLoading) return <p style={{ padding: 24 }}>Loading…</p>;
  if (error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(error as Error).message}</p>;

  return (
    <div>
      <div style={{ display: "flex", gap: 28, margin: "4px 0 18px", flexWrap: "wrap", alignItems: "flex-end" }}>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Active templates</div><div style={{ font: "600 20px Inter" }}>{stats.active}</div></div>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Due now</div><div style={{ font: "600 20px Inter", color: stats.due > 0 ? "var(--status-warning)" : "var(--fg)" }}>{stats.due}</div></div>
        <div title="Monthly-normalized recurring revenue across active templates.">
          <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>MRR (approx)</div><div style={{ font: "600 20px Inter" }}>{inr(stats.mrr)}</div>
        </div>
        {canWrite && (
          <button onClick={runGenerate} disabled={generate.isPending || stats.due === 0}
            style={{ marginLeft: "auto", padding: "7px 14px", background: stats.due > 0 ? "var(--accent-purple)" : "var(--border)", color: "#fff", border: 0, borderRadius: 8, cursor: stats.due > 0 ? "pointer" : "default", fontSize: 13 }}>
            {generate.isPending ? "Generating…" : `Generate due (${stats.due})`}
          </button>
        )}
      </div>
      {genNote && <p style={{ fontSize: 13, color: genNote.startsWith("Generated") ? "var(--status-good)" : "var(--fg-muted)", margin: "0 0 14px" }}>{genNote}</p>}

      {canWrite && <CreateTemplate />}

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <ExportButton name="Recurring Invoices" rows={() => (data ?? []).map((t) => ({
          Customer: t.customer_name, Cadence: t.cadence, Amount: Number(t.taxable_amount) + Number(t.tax_amount),
          "Next run": t.next_run_date, "Last run": t.last_run_date ?? "", Active: t.is_active ? "yes" : "no",
        }))} />
      </div>

      <div className="sanctuary-scroll-x">
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
            <th style={th}>Customer</th><th style={th}>Cadence</th><th style={{ ...th, textAlign: "right" }}>Amount</th>
            <th style={th}>Next run</th><th style={th}>Last run</th><th style={th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((t) => <TemplateRow key={t.id} t={t} />)}
          {(data ?? []).length === 0 && (
            <tr><td style={{ ...td, color: "var(--fg-muted)" }} colSpan={6}>No recurring templates yet.</td></tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}
